// `shortlist-poc/extension-alert-tab/extension-alert-tab.tpl.html` — one alert in a list.
//
// It appears in two places: the FullMode column of alerts, and the slim dropdown panel, which
// is what needs it here (plan §7 phase 3, slice 2).
//
// BOTH STATES ARE PORTED NOW. The template branches on `ExtensionAlertContainerState`: VIEW
// shows the alert's name and its result count; EDIT drops the name entirely, replaces the count
// with a prediction — "Matched candidates", the total, a progress bar of top matches against
// it, and Save / Cancel — and is what the edit-alert screen's left rail renders.
//
// ⚠️ THE PREDICTION IS PUSHED, NOT FETCHED. `onPredictionChange(prediction, topMatches)` is
// emitted by the criteria FORM as the user edits, through
// `CandidateHunterContainerLoaded.predictionData` — there is no endpoint this component can
// call for it. So `prediction` arrives as a prop, and when it is absent the tab renders
// upstream's OWN spinner branch (`ng-if="topMatchesEditStateDisplay == null"`), which is
// exactly what the live tenant shows before the first prediction lands. That is a real state
// rather than an invented one, and it is honest about the criteria form not yet publishing.
import type { ReactElement } from 'react'
import type { SlimHeaderAlert } from './slimHeaderControls.ts'

/** `ExtensionAlertContainerState`. */
export const AlertTabState = {
  View: 'view',
  Edit: 'edit',
} as const
export type AlertTabState = (typeof AlertTabState)[keyof typeof AlertTabState]

/** `HunterAutomationMode`, as the tab's three badge variants read it. */
export const AutomationMode = {
  Enabled: 'Enabled',
  Paused: 'Paused',
} as const
export type AutomationMode = (typeof AutomationMode)[keyof typeof AutomationMode]

/** `IEntityAlertNumbers` — the counts this tab shows. Absent means still loading. */
export interface AlertTabData {
  totalResults: number
  totalNewTopMatchesResults: number
}

/** `ShortlistPredictionChangeEvent` — what the criteria form publishes while editing. */
export interface AlertTabPrediction {
  /** `totalMatchesEditStateDisplay` — the big number, and the progress bar's MAX. */
  prediction: number
  /** `topMatchesEditStateDisplay` — the bar's VALUE, and the caption below it. */
  topMatches: number
}

export interface ExtensionAlertTabProps {
  alert: SlimHeaderAlert & { automationMode?: AutomationMode | null }
  state?: AlertTabState
  /** `$stateParams.alertId` — the tab for it gets `selected`. */
  currentAlertId?: number
  tabData?: AlertTabData
  /** The live prediction. Absent renders upstream's spinner — see the note at the top. */
  prediction?: AlertTabPrediction
  onSave?: () => void
  onCancel?: () => void
  /** `shortlistHunterAutomationService.isEnabled` — gates the automation badge entirely. */
  automationEnabled?: boolean
  /**
   * `ctrl.entityTypeDisplayPlural` — `Humanize.pluralize(2, getEntityTypeDisplay(product))`.
   *
   * ⚠️ THE CONTACT WORD IS "client contacts", NOT "contacts", and this doc said otherwise until
   * the contact builder's reference showed "Matched client contacts" against our "Matched
   * candidates". `getEntityTypeDisplay` returns 'candidate', 'client contact', or 'result' for
   * an unknown product — three values, and the middle one is two words. The product's DISPLAY
   * name elsewhere in the frame is "Contacts", which is what makes the shorter guess plausible.
   *
   * A prop rather than a constant for the same reason `AlertEntity` is: Phase 4 renders this
   * component with contacts, and a hardcoded noun is exactly the kind of thing that makes a
   * "mirror" phase expensive.
   */
  entityPlural?: string
}

export function ExtensionAlertTab({
  alert,
  state = AlertTabState.View,
  currentAlertId,
  tabData,
  prediction,
  onSave,
  onCancel,
  automationEnabled = false,
  entityPlural = 'candidates',
}: ExtensionAlertTabProps): ReactElement {
  const isHostSearch = typeof alert.hostSite === 'string' && alert.hostSite.length > 0
  const isEdit = state === AlertTabState.Edit
  const isSelected = currentAlertId !== undefined && currentAlertId === alert.id
  const newMatches = tabData?.totalNewTopMatchesResults ?? 0

  return (
    <div
      className={[
        'extension-alert-tab-container',
        isEdit ? 'tab-edit-state' : '',
        isHostSearch ? 'host-search-tab-container' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {isEdit ? null : (
        <div className={`extension-alert-tab-name${isSelected ? ' selected' : ''}`} title={alert.name}>
          <span className="extension-alert-tab-name-inner">{alert.name}</span>
          {automationEnabled ? (
            <span className="hunter-automation">
              {/* Three mutually exclusive badges, and the FIRST is the absence of a mode
                  rather than a mode of its own — `ng-if="!automationMode"`. Reading it as a
                  third enum value would leave an alert with no mode showing nothing. */}
              {alert.automationMode === undefined || alert.automationMode === null ? (
                <span className="btn btn-primary hunter-automation-mode mode-disabled" title="Automation disabled" />
              ) : null}
              {alert.automationMode === AutomationMode.Enabled ? (
                <span className="btn btn-primary hunter-automation-mode mode-enabled" title="Automation enabled" />
              ) : null}
              {alert.automationMode === AutomationMode.Paused ? (
                <span className="btn btn-primary hunter-automation-mode mode-paused" title="Automation paused" />
              ) : null}
            </span>
          ) : null}
          {/* Upstream's comment: "must be last in parent because of negative margin". It is
              `ng-show`, not `ng-if` — the element is always in the DOM and merely hidden — but
              nothing here measures it, and an absent node and a `display:none` one occupy the
              same zero space, so the stricter form is kept. */}
          {newMatches > 0 ? (
            <span className="extension-alert-tab-new-indicator" title={`New ${entityPlural}`}>
              {newMatches}
            </span>
          ) : null}
        </div>
      )}

      <div className="extension-alert-tab-candidate-number-cta-container">
        {isHostSearch ? <div className="host-search-tab-explanation truncate">{alert.name}</div> : null}
        <div className="extension-alert-tab-candidate-number">
          {isEdit ? null : (
            <div>
              {tabData === undefined ? (
                // Upstream shows the spinner until the counts arrive, per tab — they are
                // fetched separately from the alerts themselves.
                <div>
                  <i className="fa fa-spinner fa-spin" />
                </div>
              ) : (
                <div>
                  <div className="extension-alert-tab-content extension-alert-tab-content-secondary">
                    <i className="extension-alert-tab-icon fas fa-user extension-alert-tab-candidate-icon" />
                    <span>
                      {tabData.totalResults} {entityPlural}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
          {isEdit ? (
            <div className="prediction-edit-mode">
              {prediction === undefined ? (
                <div className="spinner-container">
                  <i className="fa fa-spinner fa-spin" />
                </div>
              ) : (
                <div>
                  <div className="extension-alert-tab-prediction-container">
                    {/* `Humanize.pluralize(2, entityTypeDisplay)` — "candidates" / "contacts". */}
                    <div className="prediction-matched-candidates">Matched {entityPlural}</div>
                    {/* ⚠️ `prediction-color-standard` IS THE RESTING CLASS, not a variant. The
                        other two (`-lower`, `-higher`) flash for 1500ms when the number MOVES,
                        which needs the previous value; without the live feed there is no
                        previous value, so the resting class is the whole of what applies. */}
                    <span className="extension-alert-tab-prediction prediction-color-standard">
                      {prediction.prediction}
                    </span>
                    {/* `uib-progressbar value max`, rendered: bootstrap's own two elements. */}
                    <div
                      className={`progress-bar-container mT5${
                        prediction.topMatches > 0 ? '' : ' progress-bar-no-matches'
                      }`}
                    >
                      <div className="progress">
                        <div
                          className="progress-bar"
                          role="progressbar"
                          aria-valuenow={prediction.topMatches}
                          aria-valuemin={0}
                          aria-valuemax={prediction.prediction}
                          style={{
                            width:
                              prediction.prediction > 0
                                ? `${String((prediction.topMatches / prediction.prediction) * 100)}%`
                                : '0%',
                          }}
                        />
                      </div>
                    </div>
                    <div className="bold-soft prediction-top-matched-candidates">
                      {prediction.topMatches} Top matches
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
        {isEdit ? (
          <div className="extension-alert-tab-edit-cta">
            <div className="extension-alert-tab-save">
              <button type="button" className="btn btn-primary" onClick={onSave}>
                Save
              </button>
            </div>
            <div className="tab-alert-cancel">
              <a
                href=""
                onClick={(event) => {
                  event.preventDefault()
                  onCancel?.()
                }}
              >
                Cancel
              </a>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}
